import { Box, Button, ButtonGroup, Divider, Drawer, Heading, IconButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  isEligibleForTourUpsell,
  TourSchedulingUpsell,
  useTouringTreatmentForZuidAndListing,
  useTourUpsellCookie,
} from '@zg-rentals/touring';
import { SatelliteTouringStatus } from '@zg-rentals/ts-satellite-client';
import useDevices from '../../../hooks/useDevices';
import useQueryParamsNext from '../../../hooks/useQueryParamsNext';
import {
  emitLeadMgmtTourUpsellBannerClickEvent,
  LEAD_MNGMT_TOUR_UPSELL_BANNER_CLICK_EVENT_TYPE_ID,
} from '../../../constants/clickstreamEvents/tourScheduling';
import { OPEN_LEAD_PROFILE_CARD_EVENT_TYPE_ID } from '../../../constants/clickstreamEvents/renterDetailsModal';
import { emitClickOptionsFromOtherActionsMenuEvent } from '../../../constants/clickstreamEvents/otherActionsMenu';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import useLeadManagementTableQuery, { useLeadLookupByConversationId } from '../../../hooks/useLeadManagementTableQuery';
import LeadJumpMenu from './components/LeadJumpMenu';
import RenterDetailsHeader from '../../RenterDetails/RenterDetailsHeader';
import RenterDetailsProgressBar from '../../RenterDetails/RenterDetailsProgressBar';
import RenterDetailsScreeningCriteria from '../../RenterDetails/RenterDetailsScreeningCriteria';
import RenterDetailsLatestContact from '../../RenterDetails/RenterDetailsLatestContact';
import RenterDetailsNotepad from '../../RenterDetails/RenterDetailsNotepad';
import NextStepTextButton from '../../LeadManagementTable/components/NextStepTextButton';
import NextStepHelperText from '../../LeadManagementTable/components/NextStepHelperText';
import { leadManagementRenterDetailsSectionToScrollToStore } from '../../../lib/leadManagementStores';
import RenterDetailsTour from '../../RenterDetails/RenterDetailsTour';
import { useAuth } from '../../../AuthProvider';
import useModal from '../../../hooks/useModal';
import { ModalType } from '../../../types/ModalType';
import useLeadManagementSecondaryActions from '../../../hooks/useLeadManagementSecondaryActions';
import useLeadRestrictions from '../../../hooks/useLeadRestrictions';
import RenterDetailsAlerts from '../../RenterDetails/RenterDetailsAlerts';
import formatListingAddress from '../../../utils/formatListingAddress';
import getRenderableNextStepLink from '../../../utils/getRenderableNextStepLink';
import StringConstants from '../../../constants/StringConstants';
import { drawerBackdropStyles, drawerBodyStyles, drawerContentStyles } from './styles';

import '@zg-rentals/touring/src/styles/tourCalendar.scss';

// Non-modal so the page and messaging window beside the drawer stay in the a11y tree. Opting out
// renders Floating UI's outside tab guards, whose focus dismisses unless closeOnFocusOut is off.
const focusManagerProps = {
  modal: false,
  closeOnFocusOut: false,
} satisfies { modal: boolean; closeOnFocusOut: boolean };

export default function RenterDetailsModal() {
  const { updateQueryParams } = useQueryParamsNext();
  const {
    clientTimeZone,
    conversationId: conversationIdFromUrl,
    conversationIdToFilterBy,
    folder,
    listingAliases,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
    source,
  } = useContext(LeadManagementContext);

  // Pass the multi-listing filter so this query shares its cache key (and lead list) with the
  // table — otherwise prev/next would page through an unfiltered all-listings list.
  const { leads, isLoading, data } = useLeadManagementTableQuery({
    clientTimeZone,
    conversationId: conversationIdToFilterBy,
    listingAliases,
    folder,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
    source,
  });

  // Listing identity comes from the open lead, not page-level context (the context alias is the
  // page filter — comma-joined under multi-listing selection).
  const currentLeadIndex = leads.findIndex((lead) => lead.conversationId === conversationIdFromUrl);
  // Content falls back to a conversation-scoped lookup when the open lead isn't in the filtered
  // list (e.g. its property was deselected while the drawer is open) — prev/next navigation stays
  // list-based and its buttons already disable in that state.
  const lookupLead = useLeadLookupByConversationId(conversationIdFromUrl, clientTimeZone, {
    enabled: !isLoading && !!data && currentLeadIndex === -1 && !!conversationIdFromUrl,
  });
  const currentLead = (currentLeadIndex !== -1 ? leads[currentLeadIndex] : null) ?? lookupLead;
  const currentLeadListingAlias = currentLead?.listingDetails?.listingAlias ?? '';
  const currentLeadListingAddress = formatListingAddress(currentLead?.listingDetails?.address);

  const { hasUpsellCookieSetForListing } = useTourUpsellCookie(currentLeadListingAlias);

  const leadManagementRenterDetailsSectionToScrollTo = leadManagementRenterDetailsSectionToScrollToStore.useValue();

  const { data: touringTreatmentData } = useTouringTreatmentForZuidAndListing(
    {
      listingAlias: currentLeadListingAlias,
    },
    {
      enabled: !hasUpsellCookieSetForListing && !!currentLeadListingAlias,
      errorDetails: 'RenterDetailsModal - Error fetching touring treatment',
    },
  );

  const [resetAllInlineFeedbackChildState, setResetAllInlineFeedbackChildState] = useState(false);
  const [shouldSaveNoteText, setShouldSaveNoteText] = useState(false);
  // Mirrors the scroll-to-section union. `renterDetailsHeader` scrolls to the top rather than
  // highlighting a section, so it never matches the `sectionToHighlight === …` checks below.
  const [sectionToHighlight, setSectionToHighlight] = useState<
    'latestContact' | 'notepad' | 'renterDetailsHeader' | 'renterProfile' | ''
  >('');
  const [isBodyRendered, setIsBodyRendered] = useState(false);
  const [isTourWizardOpen, setIsTourWizardOpen] = useState(false);
  const notepadSectionRef = useRef<HTMLDivElement>(null);
  const latestContactSectionRef = useRef<HTMLElement>(null);
  const renterDetailsHeaderRef = useRef<HTMLDivElement>(null);
  const screeningCriteriaSectionRef = useRef<HTMLParagraphElement>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { user } = useAuth();
  const { clearModal, setActiveModal } = useModal();
  const { isDesktopView } = useDevices();
  const scrollToSections = {
    notepad: notepadSectionRef,
    latestContact: latestContactSectionRef,
    renterProfile: screeningCriteriaSectionRef,
  };

  const scrollToTop = {
    renterDetailsHeader: renterDetailsHeaderRef,
  };

  const isLoadingLeadsData = isLoading || !data;

  // The drawer renders its body inside a portal (FloatingPortal) that mounts a render-cycle after
  // the modal, so the section refs aren't attached on the modal's first commit. Flip this once the
  // body node mounts so the scroll effect re-runs with the refs in place — without it a cold open
  // (e.g. "Add note") scrolls before the target exists and, since the section variable doesn't
  // change again, never retries.
  const handleBodyRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setIsBodyRendered(true);
    }
  }, []);

  useEffect(() => () => clearTimeout(highlightTimeoutRef.current), []);

  useEffect(() => {
    if (isLoadingLeadsData || !leadManagementRenterDetailsSectionToScrollTo) return;

    const ref = scrollToSections[leadManagementRenterDetailsSectionToScrollTo as keyof typeof scrollToSections];
    const headerRef = scrollToTop[leadManagementRenterDetailsSectionToScrollTo as keyof typeof scrollToTop];

    if (ref) {
      scrollToSection(ref);
    }
    if (headerRef) {
      scrollToTopOfModal(headerRef);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [leadManagementRenterDetailsSectionToScrollTo, isLoadingLeadsData, isBodyRendered]);

  const handleApproveClick = useCallback(() => {
    emitClickOptionsFromOtherActionsMenuEvent({
      user,
      listing: { listingAlias: currentLeadListingAlias },
      triggerSourceName: 'approve',
      triggerObjectName: 'lead_profile_card',
      lead: { leadId: currentLead?.renterInfo?.leadId },
    });
    setActiveModal(ModalType.ApproveLeadModal);
  }, [user, currentLeadListingAlias, currentLead?.renterInfo?.leadId, setActiveModal]);

  const secondaryActions = useLeadManagementSecondaryActions(currentLead?.nextStepSecondaryActions, {
    onApprove: handleApproveClick,
  });

  const {
    canRequestRenterProfile,
    canSendMessage,
    canViewRenterJourney,
    canViewScreeningCriteria,
    canViewTourDetails,
  } = useLeadRestrictions(currentLead);

  if (isLoadingLeadsData) {
    return null;
  }

  const touringStatus = currentLead?.touring.touringStatus;
  const shouldShowRenterDetailsTour =
    (touringStatus === SatelliteTouringStatus.ENABLED || touringStatus === SatelliteTouringStatus.UNAVAILABLE) &&
    !!currentLeadListingAlias &&
    !!conversationIdFromUrl &&
    canViewTourDetails;
  const shouldShowTourUpsellBanner =
    isEligibleForTourUpsell(touringTreatmentData) &&
    !currentLead?.applicationInfo?.isApplicationCompleted &&
    canViewTourDetails;
  const leadsSize = leads.length;

  const scrollToSection = (ref: React.RefObject<HTMLElement>) => {
    if (!ref.current) return null;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: leadManagementRenterDetailsSectionToScrollTo === 'latestContact' ? 'center' : 'start',
    });

    setSectionToHighlight(leadManagementRenterDetailsSectionToScrollTo);
    leadManagementRenterDetailsSectionToScrollToStore.set('');

    clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => {
      setSectionToHighlight('');
    }, 3000);
  };

  const scrollToTopOfModal = (headerRef: React.RefObject<HTMLElement>) => {
    if (!headerRef.current) return null;
    headerRef.current.scrollIntoView();
    leadManagementRenterDetailsSectionToScrollToStore.set('');
  };

  const handleResetAllInlineFeedbackChildStateComplete = () => {
    setResetAllInlineFeedbackChildState(false);
  };

  const handleNoteSavingComplete = () => {
    setShouldSaveNoteText(false);
  };

  const handleResetNotesInlineFeedbackAndSaveNoteText = () => {
    setResetAllInlineFeedbackChildState(true);
    setShouldSaveNoteText(true);
  };

  const handleClose = () => {
    clearModal();
    handleResetNotesInlineFeedbackAndSaveNoteText();
    updateQueryParams({
      queryParamValues: {
        conversationId: null,
      },
      shouldReplaceHistory: true,
    });
  };

  const navigateToLead = (targetConversationId: string) => {
    handleResetNotesInlineFeedbackAndSaveNoteText();
    updateQueryParams({
      queryParamValues: {
        conversationId: targetConversationId,
      },
      shouldReplaceHistory: true,
    });
    leadManagementRenterDetailsSectionToScrollToStore.set('renterDetailsHeader');
  };

  const handleNavigateToNextLead = () => {
    if (currentLeadIndex < leadsSize - 1) {
      navigateToLead(leads[currentLeadIndex + 1].conversationId);
    }
  };

  const handleNavigateToPreviousLead = () => {
    if (currentLeadIndex > 0) {
      navigateToLead(leads[currentLeadIndex - 1].conversationId);
    }
  };

  const handleUpsellBannerClick = () => {
    emitLeadMgmtTourUpsellBannerClickEvent(user, { listingAlias: currentLeadListingAlias });
  };

  const handleJumpToLead = (targetConversationId: string) => {
    if (targetConversationId === conversationIdFromUrl) return;
    navigateToLead(targetConversationId);
  };

  const header = (
    <Box
      data-eventid={OPEN_LEAD_PROFILE_CARD_EVENT_TYPE_ID}
      data-eventdescription="Open lead profile card"
      css={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        paddingInlineStart: 'looser',
      }}
    >
      <ButtonGroup
        aria-label="view previous or next renter details"
        css={{ position: 'absolute', left: 'layout.looser', top: '50%', transform: 'translate(-60%, -50%)' }}
      >
        <IconButton
          shape="circle"
          emphasis="bare"
          icon={<IconChevronUpFilled />}
          title="Previous"
          size="sm"
          onClick={handleNavigateToPreviousLead}
          disabled={currentLeadIndex <= 0}
        />
        <IconButton
          shape="circle"
          emphasis="bare"
          icon={<IconChevronDownFilled />}
          title="Next"
          size="sm"
          onClick={handleNavigateToNextLead}
          disabled={currentLeadIndex === -1 || currentLeadIndex === leadsSize - 1}
        />
      </ButtonGroup>
      {/* Desktop-gated (≥1280px) for parity with the resize affordance. The overlap this guards
          against — the absolutely-positioned menu colliding with the centered heading — only
          occurs in the sub-768px full-width drawer; at 769–1279px the menu would fit, but the
          drawer keeps the simpler header there like the window keeps its fixed layout. */}
      {isDesktopView && (
        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `left: 88px` falls between `1800` (72px) and `2400` (96px) on the numeric spacing scale
        <Box css={{ position: 'absolute', left: '88px', top: '50%', transform: 'translateY(-50%)' }}>
          <LeadJumpMenu leads={leads} currentLeadIndex={currentLeadIndex} onNavigateToLead={handleJumpToLead} />
        </Box>
      )}
      <Heading level={2} textStyle="heading-xs">
        Renter details
      </Heading>
    </Box>
  );

  const body = (
    <Box
      ref={handleBodyRef}
      css={{
        paddingBottom: 'layout.loosest',
        md: {
          paddingInline: 'loose',
        },
      }}
    >
      <RenterDetailsHeader lead={currentLead} conversationId={conversationIdFromUrl} ref={renterDetailsHeaderRef} />
      {canViewRenterJourney && <RenterDetailsProgressBar lead={currentLead} />}

      <RenterDetailsAlerts alerts={currentLead?.alerts} />

      {shouldShowTourUpsellBanner && (
        <Box css={{ marginTop: 'loose' }}>
          <TourSchedulingUpsell
            data-eventid={LEAD_MNGMT_TOUR_UPSELL_BANNER_CLICK_EVENT_TYPE_ID}
            listingAlias={currentLeadListingAlias}
            modalHeaderText={currentLeadListingAddress}
            onClick={handleUpsellBannerClick}
            onWizardOpenChange={setIsTourWizardOpen}
          />
        </Box>
      )}

      {shouldShowRenterDetailsTour && <RenterDetailsTour leadId={currentLead?.renterInfo?.leadId} />}

      <RenterDetailsScreeningCriteria
        lead={currentLead}
        conversationId={conversationIdFromUrl}
        isBackgroundHighlighted={sectionToHighlight === 'renterProfile'}
        canViewScreeningCriteria={canViewScreeningCriteria}
        canRequestRenterProfile={canRequestRenterProfile}
        ref={screeningCriteriaSectionRef}
      />
      <RenterDetailsLatestContact
        lead={currentLead}
        conversationId={conversationIdFromUrl}
        isBackgroundHighlighted={sectionToHighlight === 'latestContact'}
        ref={latestContactSectionRef}
      />
      <RenterDetailsNotepad
        clientTimeZone={clientTimeZone}
        conversationId={conversationIdFromUrl}
        leadId={currentLead?.renterInfo?.leadId}
        listingAlias={currentLeadListingAlias}
        shouldRefetchStatusLabels={false}
        resetAllInlineFeedbackChildState={resetAllInlineFeedbackChildState}
        onResetAllInlineFeedbackChildState={handleResetAllInlineFeedbackChildStateComplete}
        shouldSaveNoteText={shouldSaveNoteText}
        onHandleNoteSavingComplete={handleNoteSavingComplete}
        isBackgroundHighlighted={sectionToHighlight === 'notepad'}
        ref={notepadSectionRef}
      />
    </Box>
  );

  const renderableNextStepLink = getRenderableNextStepLink(currentLead, { isButtonStyle: true });
  const shouldShowNextStep =
    !!renderableNextStepLink && (canSendMessage || renderableNextStepLink.linkId !== 'nextStepSendMessage');
  const visibleSecondaryActions = canSendMessage
    ? secondaryActions
    : secondaryActions.filter(({ action }) => action.link.linkId !== 'nextStepSendMessage');
  const hasFooterActions = shouldShowNextStep || visibleSecondaryActions.length > 0;

  const footer = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight', width: '100%', textAlign: 'right' }}>
      <ButtonGroup aria-label="Renter details actions" css={{ justifyContent: 'flex-end', width: '100%' }}>
        {visibleSecondaryActions.map(({ action, handler }) => (
          <TooltipWrapper
            key={action.link.linkId}
            wrapInTooltip={action.isEnabled === false}
            tooltipText={action.tooltip || StringConstants.TOOLTIP.UNAVAILABLE}
            tooltipPlacement="top"
          >
            <Button emphasis="outlined" tone="brand" onClick={handler} disabled={action.isEnabled === false}>
              {action.link.text}
            </Button>
          </TooltipWrapper>
        ))}
        {shouldShowNextStep && (
          <NextStepTextButton conversationId={conversationIdFromUrl} shouldDisplayAsButtonStyle={true} />
        )}
      </ButtonGroup>
      {shouldShowNextStep && (
        <NextStepHelperText conversationId={conversationIdFromUrl} isButtonStyle={true} textStyle="body-xs" />
      )}
    </Box>
  );

  return (
    <Drawer.Root
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      // No FloatingTree, so the tour wizard's own escapeKey: false can't stop this drawer closing under it.
      useDismissProps={{ outsidePress: false, escapeKey: !isTourWizardOpen }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop
          lockScroll={false}
          css={drawerBackdropStyles}
          // FloatingOverlay sets its position inline, so a class can't offset it below the IA top bar.
          style={{ top: 'var(--ia-topnav-total-height, 0px)' }}
        >
          <Drawer.Content
            css={drawerContentStyles}
            // @ts-expect-error - focusManagerProps is typed as the full FloatingFocusManagerProps,
            // whose children/context Drawer.Content supplies itself, so a partial override can't typecheck.
            focusManagerProps={focusManagerProps}
          >
            <Drawer.Header>{header}</Drawer.Header>
            <Divider />
            <Drawer.Body css={drawerBodyStyles}>{body}</Drawer.Body>
            {hasFooterActions && (
              <>
                <Divider />
                <Drawer.Footer>{footer()}</Drawer.Footer>
              </>
            )}
            <Drawer.CloseButton />
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
